// This composable is used by NavDrawer.vue to filter menu items based on user roles and permissions

export function useMenuPermissions(userRoles, userPermissions) {
  const hasDashboardAccess = computed(() => isAuthorized('dashboard.view'));

  // Check if user is authorized based on requirements
  function isAuthorized(authorization) {
    if (!authorization) return true;
    if (Array.isArray(authorization)) {
      return authorization.some((auth) => checkAuthorizationRequirement(auth));
    }
    return checkAuthorizationRequirement(authorization);
  }

  // Check if user meets a single authorization requirement (permission or role)
  function checkAuthorizationRequirement(authorization) {
    if (!authorization) return true;
    if (typeof authorization !== 'string') return false;
    if (authorization.startsWith('role:')) {
      const roleName = authorization.slice('role:'.length);
      return userRoles.value.has(roleName);
    }
    // Default: treat as permission name
    return userPermissions.value.has(authorization);
  }

  // Helper function to check if a header has any items after it
  function hasItemsAfterHeader(sectionsList, headerItem) {
    const headerIndex = sectionsList.findIndex((section) => section.header === headerItem);
    if (headerIndex === -1 || headerIndex === sectionsList.length - 1) return false;

    // Using some() for early exit when finding a visible section
    return sectionsList.slice(headerIndex + 1).some((section) => section.hasVisibleItems);
  }

  // Process menu structure to handle headers, dividers and their sections
  function processMenuStructure(items) {
    if (!items.length) return [];

    const result = [];
    let currentSection = [];
    let lastHeader = null;
    let hasItemsInCurrentSection = false;
    const sections = [];

    // Single pass to build sections
    for (const item of items) {
      if (item.header) {
        if (currentSection.length) {
          sections.push({
            header: lastHeader,
            items: currentSection,
            hasVisibleItems: hasItemsInCurrentSection,
          });
          currentSection = [];
          hasItemsInCurrentSection = false;
        }
        lastHeader = item;
      } else if (item.divider) {
        currentSection.push(item);
      } else if (!item.__pruned) {
        const hasVisibleChildren = item.children?.some((child) => !child.__pruned) ?? false;
        if (hasVisibleChildren) {
          currentSection.push({
            ...item,
            children: item.children.filter((child) => !child.__pruned),
          });
        } else if (!item.children) {
          currentSection.push(item);
        }
        hasItemsInCurrentSection = hasItemsInCurrentSection || hasVisibleChildren || !item.children;
      }
    }

    // Add the last section if it exists
    if (currentSection.length || lastHeader) {
      sections.push({
        header: lastHeader,
        items: currentSection,
        hasVisibleItems: hasItemsInCurrentSection,
      });
    }

    // Filter and build result in a single pass
    let hasAnyVisibleItems = false;
    const finalSections = sections.filter((section) => {
      if (section.hasVisibleItems) {
        hasAnyVisibleItems = true;
        return true;
      }
      return section.header && hasItemsAfterHeader(sections, section.header);
    });

    // Build the final menu structure
    for (const section of finalSections) {
      const shouldShowHeader =
        section.header &&
        (section.hasVisibleItems || finalSections.length === 1 || !hasAnyVisibleItems);

      if (shouldShowHeader) {
        if (result.length > 0) {
          result.push({ divider: true });
        }
        result.push({ header: section.header.header });
      }

      // Add non-divider items
      for (const item of section.items) {
        if (!item.divider) {
          result.push(item);
        }
      }
    }

    return result;
  }

  // Recursively filter menu based on optional `authorization` property on items/children
  function getAuthorizedMenuItems(menuItems) {
    const filterItems = (items) =>
      items
        .map((item) => {
          // Skip processing if it's a divider or header
          if (item.divider || item.header) return item;

          // If item has children, filter them first
          if (item.children?.length) {
            const filteredChildren = filterItems(item.children).filter((c) => !c.__pruned);
            if (filteredChildren.length === 0) {
              return { __pruned: true };
            }
            // If parent has `authorization`, enforce it as well
            if (!isAuthorized(item.authorization)) return { __pruned: true };
            return { ...item, children: filteredChildren };
          }

          // Leaf item: check its own `authorization` if provided
          return isAuthorized(item.authorization) ? item : { __pruned: true };
        })
        .filter(Boolean);

    return filterItems(menuItems).filter((i) => !i.__pruned);
  }

  // Filter menu items based on permissions and special cases
  function getFilteredMenuItems(items) {
    const authorizedMenuItems = getAuthorizedMenuItems(items);

    // Special case: Hide home menu if user has dashboard access
    const filteredItems = authorizedMenuItems.filter(
      (item) => !(item.id === 'menuHome' && hasDashboardAccess.value)
    );
    return processMenuStructure(filteredItems);
  }

  return {
    getFilteredMenuItems,
  };
}
